import heroImage from "./article_pics/jabench_quasar_cover.png";

const article = {
    title: "Walking an ANY.RUN Quasar PCAP Through JA Bench",
    published: "2026-05-26",
    readingTime: "9 min read",
    tags: ["PCAP", "Threat Hunting", "JA4", "JARM", "Shodan"],
    summary: "I used a Quasar RAT PCAP exported from ANY.RUN to load JA Bench, jump to packet 291, read a JA4H result, add Shodan host context, run JARM on port 443, save the findings, and export the analyst tables.",
    favorite: true,
    image: heroImage,
    repoUrl: "https://github.com/Pb-22/JA-Bench",
    content: `
# Walking an ANY.RUN Quasar PCAP Through JA Bench

I used a Quasar RAT (aka. QLNX) PCAP exported from ANY.RUN to walk through the current JA Bench workflow from start to finish. JA Bench was built to dig into JA4+ hashes and related network evidence so I could better understand the fingerprints the community uses to identify and track device behavior on the wire. The workbench gives the analyst several views that help explain packets through RFCs, IETF publications, official documentation, blogs, and other write-ups. It breaks fingerprints into readable pieces, makes inferences and conclusions from credible sources, shows packet context with protocol layers plus binary and hexadecimal views, and allows Shodan enrichment of domains and IPs for added context. JA Bench can also run a small active test to produce a JARM hash. Any hashes worth keeping can be saved to a database inside the Docker container, and those analyst tables can be exported in multiple formats for use in other tools.

The sample gave me a good row for that walkthrough. Packet 291 is an HTTP request from \`192.168.100.5:49978\` to \`208.95.112.1:80\` with the destination name \`ip-api.com\`. From there I could read the JA4H result, open the packet context, add Shodan, run JARM on port 443, and save both passive and active findings into the analyst tables.

## Start with the input choice

The top panel gives me two entry points. If I only have a single fingerprint value, I can switch to Direct Hash Analysis and work from that one hash. If I have the whole capture, I can load the PCAP and let JA Bench derive packet rows, JA family artifacts, context, matches, and enrichment options in one pass.

That same area also keeps the theme picker and the export button close to the main workflow. I like that because the operational controls stay in view instead of being scattered around the page.

![JA Bench input modes and theme controls](${require("./article_pics/jabench_quasar_input_modes.png")})

*JA Bench starts with a clear choice between loading a PCAP and analyzing a single direct hash value.*

## Go straight to packet 291

For this walkthrough I jumped to packet 291. The line reads:

\`192.168.100.5:49978 -> 208.95.112.1:80 (ip-api.com)\`

That row is useful because it carries a JA4H result and a JA4L result on a plain HTTP request. It shows that I can still do good client side fingerprint work on an HTTP packet before I ever reach the active JARM step.

![Packet 291 row in the packet list](${require("./article_pics/jabench_quasar_packet291_row_zoom.png")})

*Packet 291 is shown by itself here. The earlier rows are gone, so the source, destination, JA4H value, and JA4L value are easier to read.*

## Read the JA4H breakdown and the reasoning under it

The JA4H card turns the HTTP fingerprint into fields I can read quickly. I can see the method, HTTP version, header count, referer flag, cookie flag, and the ordered and sorted header hash segments.

![JA4H field breakdown for packet 291](${require("./article_pics/jabench_quasar_ja4h_breakdown.png")})

*The JA4H card breaks the hash into readable pieces instead of leaving it as one long string.*

That is the mechanical part. The useful part is the reasoning box below it. On this row, the sparse header shape and the missing cookie and referer context push the interpretation toward a direct fetch or tooling style request instead of normal browser navigation.

![JA4H inference and conclusion notes for packet 291](${require("./article_pics/jabench_quasar_ja4h_inferences.png")})

*The inference panel explains why the header shape looks more like a compact client or tool than a normal click-driven browser session.*

The card also keeps the raw packet row in view above the breakdown. That makes it easier to keep the interpretation tied to the packet that produced it.

## Open the footnotes from the left side

Each inference and conclusion line can open its citation from the left side. That means I can check the source material without losing the packet row I was reading.

![JA4H footnotes open in the left citation drawer](${require("./article_pics/jabench_quasar_citation_drawer_overlay_top.png")})

*The citation drawer opens from the left and lays over the panel underneath it. This crop keeps only the first few source cards so the footnotes are larger and easier to read.*

## Expand packet layers, then collapse them, then read the bytes

From the same row I opened Packet Layers first. That gives me the decoded protocol tree for the packet. The full expanded view is too tall for the article column, so I split the important parts into smaller screenshots.

![Packet 291 artifact cards above the expanded layer panel](${require("./article_pics/jabench_quasar_layers_ja4_cards_focus.png")})

*The top of the expanded row keeps the JA4H and JA4L cards tied to packet 291 before the decoded layers begin.*

![Packet 291 inference rows above the layer panel](${require("./article_pics/jabench_quasar_layers_inference_focus.png")})

*The inference rows explain what the sparse HTTP header shape suggests before I move down into the protocol tree.*

![Packet layers protocol tree for Ethernet and IP fields](${require("./article_pics/jabench_quasar_layers_protocol_tree_focus.png")})

*The Packet Layers panel is focused here on the decoded Ethernet and IP fields instead of the whole page.*

The packet context card beside it keeps the destination, IP, user agent, and enrichment controls near the decoded fields.

![Packet context beside the decoded layers](${require("./article_pics/jabench_quasar_layers_context_card_focus.png")})

*The packet context card keeps the destination and user-agent details beside the packet evidence.*

After checking the decoded layers, I collapsed Packet Layers again and opened Bytes and Hex. That makes the raw request easier to read without carrying the whole expanded protocol tree in the same screenshot.

![Bytes and hex view focused on the raw request](${require("./article_pics/jabench_quasar_bytes_hex_request_focus.png")})

*The bytes view keeps the raw request close by when I want to confirm what actually went over the wire.*

## Add host context with Shodan

The packet context panel includes a runtime key editor for Shodan. I entered the key there, collapsed the editor, and ran the lookup from the same row.

![Shodan key editor inside the packet context panel](${require("./article_pics/jabench_quasar_shodan_key_editor.png")})

*The key editor sits inside the packet context panel, so I do not need to leave the row to enable the lookup.*

The lookup is useful because it keeps two ideas separate. The hostname search tells me that \`ip-api.com\` has been observed on many IPs across time and infrastructure. The packet destination IP is still the anchor for this row, so JA Bench uses \`208.95.112.1\` for the host summary.

![Shodan hostname search for ip-api.com](${require("./article_pics/jabench_quasar_shodan_hostname_search.png")})

*The hostname search is broad context. The packet destination IP stays tied to the row.*

From that host summary I can immediately see the destination domain, location, organization, certificate issuer, and the open ports that matter for the next step.

![Shodan host summary for the packet destination](${require("./article_pics/jabench_quasar_shodan_host_summary.png")})

*The host summary ties the enrichment back to \`208.95.112.1\`, not just the broader hostname.*

In this case the host summary shows both port 80 and port 443 open. The port 80 service redirects to HTTPS, and port 443 answers normally, which is why the next active JARM step was worth trying.

![Shodan port details for ports 80 and 443](${require("./article_pics/jabench_quasar_shodan_ports.png")})

*Service detail rounds out the host view and helps decide whether an active probe is worth running.*

## Run JARM on port 443

The passive row itself is HTTP on port 80. I still ran JARM on port 443 because the host summary showed 443 open and I wanted an active TLS answer from the same named destination. That gives me a second view of the host that answers a different question from the passive HTTP row.

![JARM target controls set to ip-api.com on port 443](${require("./article_pics/jabench_quasar_jarm_target_focus.png")})

*The active JARM control stays tied to the same target host, but the port field is changed to 443 before running it.*

![JARM port field focused on 443](${require("./article_pics/jabench_quasar_jarm_port_field_focus.png")})

*The port setting is the small decision that changes this from the passive HTTP row into an active TLS fingerprint check.*

JA Bench returned a live JARM result for \`ip-api.com\` and kept the notes right under the result. That makes it easy to compare the active TLS answer with the passive packet context from the same row.

![JARM hash result for ip-api.com](${require("./article_pics/jabench_quasar_jarm_hash_focus.png")})

*The result panel keeps the returned JARM hash and target together.*

![Top half of the JARM explanatory notes](${require("./article_pics/jabench_quasar_jarm_notes_top_focus.png")})

![Bottom half of the JARM explanatory notes](${require("./article_pics/jabench_quasar_jarm_notes_bottom_focus.png")})

*Splitting the notes makes the interpretation readable without shrinking the whole panel into one screenshot.*

## Save the JARM result and the JA4H value

Once the JARM result was in place, I opened the right side drawer to save it with the target host, target IP, destination domain, port, and analyst note fields already lined up.

![Save JARM drawer focused on the prefilled target fields](${require("./article_pics/jabench_quasar_save_jarm_fields_focus.png")})

*The JARM save drawer is prefilled from the packet row and the active probe result.*

![Save JARM drawer focused on the fingerprint and analyst note fields](${require("./article_pics/jabench_quasar_save_jarm_note_focus.png")})

*The lower part of the drawer is where the actual fingerprint and local analyst note are preserved.*

I also opened the right side drawer for the JA4H value. That lets me keep the passive HTTP fingerprint in the local analyst reference table with the context that matters for later matching.

![Save JA4H drawer focused on packet and artifact fields](${require("./article_pics/jabench_quasar_save_ja4h_fields_focus.png")})

*The JA4H save flow keeps the passive fingerprint close to the packet that produced it.*

![Save JA4H drawer focused on local context and related values](${require("./article_pics/jabench_quasar_save_ja4h_context_focus.png")})

*The lower drawer fields are where analyst context and related packet values get attached before saving.*

## Export the analyst tables and rename the file

The export drawer is the last step. It lets me rename the export before I write it out, choose the format, and decide whether I want both analyst tables, only the JA reference table, or only the JARM table. Export formats at the time of this write-up are CSV, JSON, and JSONL (aka NDJSON).

That rename field matters because I usually want the output tied to a case name, malware family, or lab pass instead of accepting a generic default every time.

![Export drawer focused on rename and file format options](${require("./article_pics/jabench_quasar_export_options_focus.png")})

*The top of the export drawer keeps the rename field and file format choice together.*

![Export drawer focused on analyst table scope](${require("./article_pics/jabench_quasar_export_tables_focus.png")})

*The lower part of the export drawer controls which analyst tables are written out.*

## Why this flow works for an ANY.RUN PCAP

This is the use case I wanted JA Bench to support. I can start with a PCAP that came out of ANY.RUN, Malware-Traffic-Analysis.net or elsewhere, find the packet row that matters to me, read the passive JA family artifacts, open the decoded packet context, pull in the host context, run one active JARM check when the host justifies it, and then save or export the parts that are worth keeping.

I do not have to move between unrelated tools just to carry one row from passive evidence to analyst curation. The packet row stays at the center of the workflow, which keeps the reasoning easier to follow and easier to explain later.
`,
};

export default article;
